import {Analytics, logEvent, setUserId, setUserProperties} from 'firebase/analytics';
import {Capacitor} from '@capacitor/core';
import packageJson from '../../package.json';
import {getAnalyticsInstance} from '../firebase/analytics';

export type SongPlaySource = 'search' | 'playlist' | 'home' | 'recently_played' | 'unknown';

export type AuthMethod = 'google' | 'email' | 'register';
// Where in the sign-in flow we failed. Useful for diagnosing per-device
// breakage (e.g. Credential Manager fails on phones with work profiles, the
// legacy picker fails when google-services config is wrong, the Firebase
// credential exchange fails when the SHA-1 isn't registered).
export type AuthFailureStage =
    | 'credential_manager'   // Android: CredentialManager.getCredentialAsync failed
    | 'legacy_picker'        // Android: classic GoogleSignIn (useCredentialManager:false) failed
    | 'no_id_token'          // Native flow returned but no ID token in result
    | 'firebase_credential'  // signInWithCredential / signInWithPopup failed
    | 'email_password'       // Email/password sign-in failed
    | 'register'             // Email/password registration failed
    | 'unknown';

type EventMap = {
    song_play: {songId: string; source: SongPlaySource};
    search_submit: {query: string};
    search_result_click: {resultType: string};
    playlist_create: Record<string, never>;
    playlist_share: {playlistType: 'user' | 'api'};
    auth_sign_in: {method: AuthMethod};
    // PII-free diagnostic event. `code` is a short identifier; `message` is the
    // redacted body of the exception (emails stripped, capped at 100 chars to
    // satisfy GA4's per-param string limit). The pair is what makes this
    // event actionable — `code` lets you group failures, `message` tells you
    // which specific variant of that group you hit.
    auth_sign_in_failed: {
        method: AuthMethod;
        stage: AuthFailureStage;
        code: string;        // e.g. 'auth/invalid-credential', 'GetCredentialUnknownException', 'ApiException:10'
        message: string;     // redacted, ≤100 chars
        platform: string;    // 'web' | 'android' | 'ios'
    };
    cast_session_start: Record<string, never>;
    cast_session_end: {reason: 'user' | 'failed' | 'remote'};
};

const isProd = process.env.NODE_ENV === 'production';
let userPropertiesInitialized = false;

const ensureUserProperties = (analytics: Analytics) => {
    if (userPropertiesInitialized) return;
    userPropertiesInitialized = true;
    setUserProperties(analytics, {
        platform: Capacitor.getPlatform(), // 'web' | 'android' | 'ios' — GA4's platform field is always 'web' for Capacitor; this disambiguates.
        app_version: packageJson.version,
    });
};

/**
 * Logs a typed analytics event. Adding a new event = add it to EventMap; the
 * compiler enforces params at every call site. No-op outside production.
 */
export const track = async <K extends keyof EventMap>(
    name: K,
    params: EventMap[K],
): Promise<void> => {
    if (!isProd) {
        // eslint-disable-next-line no-console
        console.debug('[telemetry]', name, params);
        return;
    }
    const analytics = await getAnalyticsInstance();
    if (!analytics) return;
    ensureUserProperties(analytics);
    // The Firebase SDK overloads logEvent for reserved event names; cast via
    // string to use it for arbitrary custom events typed by our EventMap.
    logEvent(analytics, name as string, params as Record<string, unknown>);
};

/** Pass uid on sign-in, null on sign-out. Enables cross-device user identity in GA4. */
export const setTelemetryUser = async (uid: string | null): Promise<void> => {
    if (!isProd) return;
    const analytics = await getAnalyticsInstance();
    if (!analytics) return;
    setUserId(analytics, uid);
};
